import { HighlightedText } from "./HighlightedText";
import { useEffect, useState } from "react";
import { usePapaParse } from "react-papaparse";

interface Props {
  content: string;
  searchTerm: string;
  isFocused?: boolean;
  showMoreByDefault?: boolean;
}

export function ExcelSnippet(props: Props) {
  const { content, searchTerm, isFocused = false, showMoreByDefault = false } = props;

  const { readString } = usePapaParse();
  const [parsedRows, setParsedRows] = useState<string[][]>([]);

  const [showMore, setShowMore] = useState(showMoreByDefault);

  useEffect(() => {
    setShowMore(isFocused || showMoreByDefault);
  }, [isFocused, showMoreByDefault]);

  useEffect(() => {
    readString(content, {
      skipEmptyLines: false,
      complete: (results) => {
        const trimmedData = trimTable(results.data as string[][]);
        setParsedRows(trimmedData);
      },
    });
  }, [content]);

  const trimmedRows = showMore
    ? parsedRows
    : parsedRows.slice(0, Math.min(parsedRows.length, 3));

  return (
    <div className="mt-2 relative group">
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full border-collapse rounded-md">
          <tbody>
            {trimmedRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`px-2 py-1 border border-gray-100 ${
                      rowIndex === 0 || cellIndex === 0
                        ? "font-semibold"
                        : "font-normal"
                    }`}
                  >
                    <HighlightedText
                      text={cell}
                      searchTerm={searchTerm}
                      className="text-sm text-gray-700"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <>
        {/* Gradient shadow overlay */}
        {!showMore && parsedRows.length > 3 && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        )}
      </>
    </div>
  );
}

function trimTable(table: string[][]): string[][] {
  // If table is empty, return empty array
  if (table.length === 0) return [];

  // Find the first non-empty row from the beginning
  let firstNonEmptyRow = -1;
  for (let row = 0; row < table.length; row++) {
    if (table[row].some((cell) => cell.trim() !== "")) {
      firstNonEmptyRow = row;
      break;
    }
  }

  // Find the last non-empty row from the end
  let lastNonEmptyRow = -1;
  for (let row = table.length - 1; row >= 0; row--) {
    if (table[row].some((cell) => cell.trim() !== "")) {
      lastNonEmptyRow = row;
      break;
    }
  }

  // If no non-empty rows found, return empty array
  if (firstNonEmptyRow === -1 || lastNonEmptyRow === -1) return [];

  // Trim rows from beginning and end, keeping all rows in between
  let trimmedTable = table.slice(firstNonEmptyRow, lastNonEmptyRow + 1);

  // Find the last non-empty column
  const maxCols = Math.max(...trimmedTable.map((row) => row.length));
  let lastNonEmptyCol = -1;

  // Check each column from right to left
  for (let col = maxCols - 1; col >= 0; col--) {
    const hasNonEmptyCell = trimmedTable.some(
      (row) => row[col] && row[col].trim() !== "",
    );
    if (hasNonEmptyCell) {
      lastNonEmptyCol = col;
      break;
    }
  }

  // Trim columns after the last non-empty column
  if (lastNonEmptyCol >= 0) {
    trimmedTable = trimmedTable.map((row) => row.slice(0, lastNonEmptyCol + 1));
  }

  return trimmedTable;
} 